import type Graph from 'graphology';
import forceAtlas2 from 'graphology-layout-forceatlas2';
import FA2Layout from 'graphology-layout-forceatlas2/worker';
import noverlap from 'graphology-layout-noverlap';
import type Sigma from 'sigma';

import {
  getFA2Settings,
  getLayoutDuration,
  getNovellapSettings,
} from '../lib/graph-layout-settings.js';
import type { SigmaEdgeAttributes, SigmaNodeAttributes } from '../lib/graph-node-attributes.js';

export interface LayoutController {
  start: (graph: Graph<SigmaNodeAttributes, SigmaEdgeAttributes>) => void;
  stop: (graph: Graph<SigmaNodeAttributes, SigmaEdgeAttributes> | null) => void;
  dispose: () => void;
}

export function createLayoutController(
  sigmaRef: { current: Sigma | null },
  onRunningChange: (running: boolean) => void,
): LayoutController {
  let layout: FA2Layout | null = null;
  let timeout: ReturnType<typeof setTimeout> | null = null;

  const clearTimer = (): void => {
    if (timeout) {
      clearTimeout(timeout);
      timeout = null;
    }
  };

  const start = (graph: Graph<SigmaNodeAttributes, SigmaEdgeAttributes>): void => {
    const nodeCount = graph.order;
    if (nodeCount === 0) return;
    layout?.kill();
    layout = null;
    clearTimer();
    const duration = getLayoutDuration(nodeCount);
    if (duration === 0) {
      // Pre-seeded positions are sufficient; noverlap throws RangeError at this scale.
      sigmaRef.current?.refresh();
      onRunningChange(false);
      return;
    }
    const settings = { ...forceAtlas2.inferSettings(graph), ...getFA2Settings(nodeCount) };
    layout = new FA2Layout(graph, { settings });
    layout.start();
    onRunningChange(true);
    timeout = setTimeout(() => {
      if (!layout) return;
      layout.stop();
      layout = null;
      noverlap.assign(graph, getNovellapSettings(graph.order));
      sigmaRef.current?.refresh();
      onRunningChange(false);
    }, duration);
  };

  const stop = (graph: Graph<SigmaNodeAttributes, SigmaEdgeAttributes> | null): void => {
    clearTimer();
    if (!layout) return;
    layout.stop();
    layout = null;
    if (graph) {
      noverlap.assign(graph, getNovellapSettings(graph.order));
      sigmaRef.current?.refresh();
    }
    onRunningChange(false);
  };

  const dispose = (): void => {
    clearTimer();
    layout?.kill();
    layout = null;
  };

  return { start, stop, dispose };
}
